import {
  Section,
  Work,
  WorkAnalysis,
  BaseWork,
  WorkAnalysisState,
  Chapter,
} from "@/app/types/book";
import { SupabaseClient } from "@supabase/supabase-js";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { LLMMetadata } from "../llm/types";
import { parseJSON } from "../parserUtils";
import { Cacheable } from "@/lib/cache";
import WorkFactory from "./workFactory";
import {
  getActiveProducts,
  getProductById,
  getProductByCodeName,
  purchaseProduct,
  getUserProducts as repoGetUserProducts,
  getProductTier,
  getProductsByPaymentTier,
  getOrderById,
  getOrdersForWork,
  getUnassociatedOrderForUser,
  getUnassociatedOrderWithProductForUser,
  getTierProducts
} from "./repositories/ProductsRepository";
import {
  createFeedback,
  getFeedbackForUser,
  getFeedbackById,
  updateFeedback,
  deleteFeedback,
  getFeedbackByLocation,
  getAnalysisFeedbackStats
} from "./repositories/FeedbackRepository";
import {
  createWork as repoCreateWork,
  updateWork as repoUpdateWork,
  getWorksForUser as repoGetWorksForUser,
  getWorkByTitle as repoGetWorkByTitle,
  getWorkById as repoGetWorkById,
  getWorkByFriendlyId as repoGetWorkByFriendlyId,
  getCurrentDemoBook,
} from "./repositories/WorksRepository";
import {
  getStaticContentKeys as repoGetStaticContentKeys,
  getStaticContentByKey as repoGetStaticContentByKey,
  saveStaticContent as repoSaveStaticContent,
} from "./repositories/StaticContentRepository";
import {
  createUserProfileIfNotExists as repoCreateUserProfileIfNotExists,
  isUserAdmin as repoIsUserAdmin,
  invalidateUserCache as repoInvalidateUserCache,
  clearUserCaches as repoClearUserCaches,
  approveUser as repoApproveUser,
} from "./repositories/UserProfilesRepository";
import {
  handleSuccessfulPayment as repoHandleSuccessfulPayment,
  associatedWorkWithOrderId as repoAssociatedWorkWithOrderId,
  handleFailedPayment as repoHandleFailedPayment,
  handlePaymentEventReceived as repoHandlePaymentEventReceived,
  verifyPayment as repoVerifyPayment,
} from "./repositories/PaymentsRepository";
import {
  addBackgroundTask as repoAddBackgroundTask,
  updateBackgroundTask as repoUpdateBackgroundTask,
  getNextPendingTask as repoGetNextPendingTask,
  getBackgroundTask as repoGetBackgroundTask,
  getLatestWorkflowRun as repoGetLatestWorkflowRun,
} from "./repositories/BackgroundTasksRepository";
import {
  getProductCode as repoGetProductCode,
  redeemProductCode as repoRedeemProductCode,
} from "./repositories/ProductCodesRepository";
import {
  getActiveSiteNotifications as repoGetActiveSiteNotifications,
} from "./repositories/SiteNotificationsRepository";
import {
  createEmailSignup as repoCreateEmailSignup,
  CreateEmailSignupResult,
} from "./repositories/EmailSignupsRepository";
import { PaymentTier } from "@/app/types/payment";
import {
  BackgroundTask,
  BackgroundTaskStatus,
  TaskParameters,
  TaskType,
} from "@/app/types/tasks";
import { toTitleCase } from "titlecase";
import { Order, Product, ProductWithTaskStatus } from "@/app/types/products";
import { ProductCode } from "@/app/types/productCodes";
import { Feedback } from "@/app/types/feedback";
import { v4 } from "uuid";
import { getUserProductsForWork } from "./repositories/ProductsRepository";

// Factory for creating specific work type instances

export class WorkDb {

  private static client: SupabaseClient | null = null;

  /*
   * Service user id for background tasks.
   */
  public static readonly backgroundTaskClientId: string =
    "00000000-0000-0000-0000-000000000000";

  protected static getClientInstance(): SupabaseClient | null {
    return WorkDb.client;
  }

  protected static setClientInstance(client: SupabaseClient) {
    WorkDb.client = client;
  }

  /**
   * Initialize the database connection with a client
   * @param client - Optional Supabase client to use
   * @param force - Force reinitialization even if client exists
   */
  public static initialize(
    client?: SupabaseClient,
    force: boolean = false
  ): SupabaseClient {
    if (force || !WorkDb.client) {
      WorkDb.client = client || createClientComponentClient();
    }
    return WorkDb.client;
  }

  /**
   * Get the authenticated user's ID from the session
   * @throws Error if user is not authenticated
   */
  public static async getAuthenticatedUserId(): Promise<string> {
    const client = WorkDb.getClientInstance();
    if (!client) {
      throw new Error("Database client is not initialized");
    }

    // Check if using service role auth by comparing with env variable
    if ((client as any).supabaseKey === process.env.SUPABASE_SERVICE_ROLE_KEY) {
      return WorkDb.backgroundTaskClientId;
    }

    const {
      data: { session },
      error,
    } = await client.auth.getSession();

    if (error) {
      throw new Error(`Authentication error: ${error.message}`);
    }

    if (!session?.user?.id) {
      throw new Error("No authenticated user");
    }

    return session.user.id;
  }

  public static getClient(): SupabaseClient {
    if (!WorkDb.client) {
      throw new Error(
        "Database client not initialized. Call initialize() first."
      );
    }
    return WorkDb.client;
  }

  /**
   * Creates a work from title, author and content
   *
   */

  public static async createWork(
    title: string,
    author: string,
    work_type: string,
    metadata: any = {},
    remote_file_metadata: any = {},
    friendlyId?: string
  ): Promise<BaseWork> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return repoCreateWork(
      WorkDb.getClient(),
      userId,
      title,
      author,
      work_type,
      metadata,
      remote_file_metadata,
      friendlyId
    );
  }

  /**
   * Updates the remote file metadata for a section.
   * @param sectionId The ID of the section.
   * @param remoteFileMetadata The new remote file metadata.
   * @returns The updated section.
   */
  public static async updateSectionRemoteFileMetadata(
    sectionId: string,
    remoteFileMetadata: any
  ) {
    const { data, error } = await WorkDb.getClient()
      .from("sections")
      .update({
        remote_file_metadata: remoteFileMetadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sectionId)
      .select("*")
      .maybeSingle();

    if (error) {
      throw new Error(`Error updating section: ${error.message}`);
    }

    if (!data) {
      throw new Error("Failed to update section");
    }

    return WorkFactory.sectionToJSON(data);
  }

  /**
   * Gets the story flow metrics for a work.
   * @param workId The ID of the work.
   * @returns The story flow metrics.
   */
  public static async getStoryFlowMetrics(workFriendlyId: string) {
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (work && !work.tags?.toLowerCase().includes("demo")) {
          return null;
        }
      } else {
        throw err;
      }
    }

    if (!work) {
      throw new Error(`Work not found with id: ${workFriendlyId}`);
    }

    if (!work) {
      throw new Error(`Work not found with id: ${workFriendlyId}`);
    }

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select(
        `
        analysis_json->>friendly_id,
        analysis_json->>chapter_number,
        analysis_json->>chapter_title,
        analysis_json->>summary,
        analysis_json->analysis->reading_score,
        analysis_json->analysis->narrative_metrics->>pacing_score,
        analysis_json->analysis->narrative_metrics->>tension_score,
        analysis_json->analysis->narrative_metrics->>emotional_intensity_score,
        analysis_json->analysis->narrative_metrics->>character_development_score,
        analysis_json->analysis->narrative_metrics->>relationship_dynamics_score,
        analysis_json->analysis->plot_points
      `
      )
      .eq("work_id", work.id)
      .like("analysis_type", "chapter-summary%")
      .order("analysis_json->>chapter_number", { ascending: true });

    if (error) {
      throw new Error(`Error fetching story flow metrics: ${error.message}`);
    }

    if (!data) {
      throw new Error("No story flow metrics found for the specified work.");
    }

    const returnChapters = data.map((row: any) => {
      const chapter = {
        friendly_id: row["friendly_id"],
        chapter_number: row["chapter_number"],
        title: row["chapter_title"],
        id: row["friendly_id"],
        summary: row["summary"],
        reading_score: row["reading_score"],
        narrative_metrics: {
          pacing_score: row["pacing_score"],
          tension_score: row["tension_score"],
          emotional_intensity_score: row["emotional_intensity_score"],
          character_development_score: row["character_development_score"],
          relationship_dynamics_score: row["relationship_dynamics_score"],
        },
        plot_points: row["plot_points"],
      } as Chapter;

      return chapter;
    });

    return returnChapters;
  }

  /**
   * Adds or updates a section.
   * @param params Object containing section data and metadata.
   */
  public static async addOrUpdateSection(
    sectionId: string | null = null,
    workId: string,
    friendlyId: string,
    title: string,
    content: string,
    orderIndex = 0,
    remoteFileMetadata = {}
  ): Promise<Section> {
    // Validate inputs
    this.validateSectionParams({ workId, friendlyId, title });

    let sectionExists = false;
    let sectionData = null;

    // Check if section exists by `sectionId` or `friendlyId`
    if (sectionId) {
      sectionData = await this.fetchSectionById(workId, sectionId);
      sectionExists = !!sectionData;
    }

    if (!sectionExists) {
      sectionData = await this.fetchSectionByFriendlyId(workId, friendlyId);
      sectionExists = !!sectionData;

      if (sectionExists) {
        sectionId = sectionData.id;
      }
    }

    // Prepare data for insertion or update
    const sectionPayload = {
      title,
      content,
      order_index: orderIndex,
      remote_file_metadata: remoteFileMetadata,
      ...(sectionExists
        ? { updated_at: new Date().toISOString() }
        : { work_id: workId, friendly_id: friendlyId }),
    } as Section;

    // Insert or update section
    let _sectionToReturn = null;
    let _error = null;
    if (sectionExists) {
      const { data, error } = await WorkDb.getClient()
        .from("sections")
        .update(sectionPayload)
        .eq("id", sectionId)
        .select("*")
        .single();

      if (error) {
        _error = error;
      } else if (data) {
        _sectionToReturn = WorkFactory.sectionToJSON(data);
      }
    } else {
      const { data, error } = await WorkDb.getClient()
        .from("sections")
        .insert(sectionPayload)
        .select("*")
        .single();
      if (error) {
        _error = error;
      } else if (data) {
        _sectionToReturn = WorkFactory.sectionToJSON(data);
      }
    }

    if (_error) {
      throw new Error(`Error adding or updating section: ${_error.message}`);
    }

    if (!_sectionToReturn) {
      throw new Error("Failed to add or update section");
    }

    return WorkFactory.sectionToJSON(_sectionToReturn);
  }

  // Helper: Validate parameters
  private static validateSectionParams({
    workId,
    friendlyId,
    title,
  }: {
    workId: string;
    friendlyId: string;
    title: string;
  }) {
    if (!workId) throw new Error("Missing workId");
    if (!friendlyId) throw new Error("Missing friendlyId");
    if (!title) throw new Error("Missing title");
  }

  // Helper: Fetch section by ID
  public static async fetchSectionById(workId: string, sectionId: string) {
    const { data, error } = await WorkDb.getClient()
      .from("sections")
      .select("*")
      .eq("work_id", workId)
      .eq("id", sectionId)
      .maybeSingle();
    if (error) return null;
    return data;
  }

  // Helper: Fetch section by friendly ID
  private static async fetchSectionByFriendlyId(
    workId: string,
    friendlyId: string
  ) {
    const { data, error } = await WorkDb.getClient()
      .from("sections")
      .select("*")
      .eq("work_id", workId)
      .eq("friendly_id", friendlyId)
      .maybeSingle();
    if (error) return null;
    return data;
  }

  public static async getStaticString(
    id: string,
    enableTokenParsing = false
  ): Promise<string> {
    const { data, error } = await WorkDb.getClient()
      .from("static_content")
      .select("*")
      .eq("id", id)
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(
        `[WorkDb.getStaticString] Error fetching static string: ${error.message}`
      );
    }

    if (!data) {
      throw new Error(
        `[WorkDb.getStaticString] No static string found for the key '${id}'. Double check that this key exists.`
      );
    }

    if (data.value && enableTokenParsing) {
      data.value = WorkDb.parseTokens(data.value);
    }

    return data.value;
  }

  /**
   * Get all works for a specific user
   *
   * This method retrieves:
   * 1. All public works created by the user
   * 2. Pre-selected demo works that are visible to all users
   *
   * @returns Promise<BaseWork[]> - Array of user and demo works
   */
  @Cacheable({
    ttl: 14400,
    keyGenerator: (userId?: string) => `user-works:${userId || 'current'}`,
  })
  public static async getWorksForUser(userId?: string): Promise<BaseWork[]> {
    const resolvedUserId = userId || (await WorkDb.getAuthenticatedUserId());
    return repoGetWorksForUser(WorkDb.getClient(), resolvedUserId);
  }

  /**
   * Updates a work with the given update parameters
   * @param workId string - The work id
   */
  public static async updateWork(
    workId: string,
    metaData: Record<string, any>
  ): Promise<BaseWork> {
    return repoUpdateWork(WorkDb.getClient(), workId, metaData);
  }

  /**
   * Get's the analysis keys for a specific work with detailed metrics
   * @param workId string - The work id
   * @returns Promise<Record<string, any>> - The analysis keys with detailed metrics
   */
  public static async getWorkAnalysisKeys(
    workId: string
  ): Promise<Record<string, any>> {


    const products = await WorkDb.getActiveProducts();

    if (!products) {
      throw new Error("Failed to fetch active products");
    }

    const productCodeNames = products.map((product) => product.code_name);

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("analysis_type, analysis_md")
      .eq("work_id", workId)
      .in("analysis_type", productCodeNames)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Error fetching work analyses: ${error.message}`);
    }

    const { data: multipleData, error: multipleError } =
      await WorkDb.getClient()
        .from("work_analyses")
        .select("analysis_type, analysis_md, created_at")
        .eq("work_id", workId)
        .or(
          "analysis_type.like.antagonist-analysis-%, analysis_type.like.genre:%, analysis_type.like.protagonist-analysis-%"
        )
        .order("created_at", { ascending: false });

    if (multipleError) {
      throw new Error(`Error fetching work analyses: ${multipleError.message}`);
    }

    // join data and multipleData
    const combinedData = [...(data || []), ...(multipleData || [])];

    const result: Record<string, any> = {};

    (combinedData || []).forEach((work) => {
      // Extract metrics from analysis_md if it exists
      if (work.analysis_md) {
        const metricsMatch = work.analysis_md.match(
          /<METRICS>([\s\S]*?)<\/METRICS>/i
        );

        if (metricsMatch && metricsMatch[1]) {
          try {
            // Parse the JSON metrics
            const metrics = parseJSON(metricsMatch[1].trim());

            // Normalize the metrics to ensure consistent structure
            const normalizedMetrics: Record<string, any> = {};

            // Process each metric
            Object.entries(metrics).forEach(
              ([key, metricData]: [string, any]) => {
                // Ensure the metric data has the expected structure
                if (metricData && typeof metricData === "object") {
                  // Normalize the value to lowercase for consistency
                  if (metricData.value) {
                    metricData.value = metricData.value.toLowerCase();
                  }

                  normalizedMetrics[key] = metricData;
                }
              }
            );

            // Add the normalized metrics to the result
            result[work.analysis_type] = normalizedMetrics;
          } catch (parseError) {
            console.error(
              `Error parsing metrics for ${work.analysis_type}:`,
              parseError
            );
            // If parsing fails, set an empty object for this analysis type
            result[work.analysis_type] = {};
          }
        } else {
          // No metrics found, set an empty object
          result[work.analysis_type] = {};
        }
      } else {
        // No analysis_md, set an empty object
        result[work.analysis_type] = {};
      }
    });

    return result;
  }

  public static async getWorkByTitle(title: string): Promise<BaseWork | null> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return repoGetWorkByTitle(WorkDb.getClient(), userId, title);
  }

  /**
   * Get a specific work by ID
   * @param workId - The ID of the work
   * @returns Promise<BaseWork> - The work object
   */
  @Cacheable({
    ttl: 3600, // 1 hour cache
    keyGenerator: (workId) => `work-by-id:${workId}`,
  })
  public static async getWorkById(workId: string): Promise<BaseWork | null> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return repoGetWorkById(WorkDb.getClient(), userId, workId);
  }

  /** Get a specific work by friendlyId */
  @Cacheable({
    ttl: 14400, // cache for 4 hours as demo pages are heavily accessed
    keyGenerator: (friendlyId) => `work-by-friendly-id:${friendlyId}`,
  })
  public static async getWorkByFriendlyId(friendlyId: string, userId?: string): Promise<Work | null> {
    const resolvedUserId = userId || (await WorkDb.getAuthenticatedUserId());
    return repoGetWorkByFriendlyId(WorkDb.getClient(), friendlyId, resolvedUserId);
  }

  /**
   * Retrieve a work by friendly ID for unauthenticated visitors.
   * Falls back to the background client and only returns works tagged `DEMO`.
   */
  public static async getPublicWorkByFriendlyId(
    friendlyId: string,
  ): Promise<Work | null> {
    try {
      return await WorkDb.getWorkByFriendlyId(friendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        const work = await WorkDb.getWorkByFriendlyId(
          friendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (work && work.tags?.toLowerCase().includes("demo")) {
          return work;
        }
        return null;
      }
      throw err;
    }
  }

  public static async getWorkAnalysisById(
    workId: string,
    analysisId: string
  ): Promise<WorkAnalysis | null> {
    if (!workId) {
      throw new Error("Missing workId");
    }

    if (!analysisId) {
      throw new Error("Missing analysisId");
    }

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", workId)
      .eq("id", analysisId)
      .maybeSingle();

    if (error) {
      throw new Error(`Error fetching work analysis: ${error.message}`);
    }

    if (!data) {
      return null;
    }

    // Transform the data to match our expected format
    return WorkFactory.analysisToJSON(data.data);
  }

  /**
   * Gets the analyses for a specific work that match the analysis types provided
   * @param workFriendlyId - The friendly ID of the work
   * @param analysisTypes - A collection of analysis types to search for
   * @returns Promise<WorkAnalysis[]> - The result of the query
   */
  @Cacheable({
    ttl: 3600, // 1 hour cache
    keyGenerator: (workFriendlyId, analysisTypes) =>
      `work-analyses-by-type:${workFriendlyId}:${analysisTypes.join(",")}`,
  })
  public static async getWorkAnalysesByAnalysisType(
    workFriendlyId: string,
    analysisTypes: string[]
  ): Promise<WorkAnalysis[]> {
    if (!workFriendlyId) {
      throw new Error("Missing workFriendlyId");
    }

    if (!analysisTypes || analysisTypes.length === 0) {
      throw new Error("Missing analysisTypes");
    }

    // First get the work to ensure we have the correct internal ID
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (work && !work.tags?.toLowerCase().includes("demo")) {
          return [];
        }
      } else {
        throw err;
      }
    }
    if (!work) {
      throw new Error("Work not found");
    }

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", work.id)
      .in("analysis_type", analysisTypes);

    if (error) {
      throw new Error(`Error fetching work analysis: ${error.message}`);
    }

    if (!data) {
      return [];
    }

    // Transform the data to match our expected format
    return (data || []).map((row) => WorkFactory.analysisToJSON(row));
  }

  public static async getGenreAnalysis(
    workFriendlyId: string
  ): Promise<WorkAnalysis | null> {
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (work && !work.tags?.toLowerCase().includes("demo")) {
          return null;
        }
      } else {
        throw err;
      }
    }
    if (!work) {
      throw new Error("Work not found");
    }

    // First, get the genre-identification analysis
    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", work.id)
      .eq("analysis_type", "genre-identification")
      .maybeSingle();

    if (error) {
      throw new Error(`Error fetching genre identification analysis: ${error.message}`);
    }

    if (!data || !data.analysis_json) {
      return null;
    }

    // Process the analysis_json to find the top subgenre
    try {
      const subGenres = Array.isArray(data.analysis_json) ? data.analysis_json : [];

      if (subGenres.length === 0) {
        return null;
      }

      // Sort by score in descending order and get the top one
      const topSubgenre = [...subGenres].sort((a, b) =>
        (Number(b.score) || 0) - (Number(a.score) || 0)
      )[0];

      if (!topSubgenre || !topSubgenre.subgenreid) {
        return null;
      }

      // Get the detailed analysis for the top subgenre
      const analysisType = `genre:${topSubgenre.subgenreid}`;
      return await WorkDb.getWorkAnalysisByAnalysisType(workFriendlyId, analysisType);
    } catch (err) {
      console.error("Error processing genre analysis:", err);
      return null;
    }
  }

  /**
   * Get a specific analysis by ID
   * @param workFriendlyId - The ID or friendly ID of the work
   * @param analysisType - The ID of the analysis
   * @returns Promise<WorkAnalysis> - The analysis object
   */
  @Cacheable({
    ttl: 3600, // 1 hour cache
    keyGenerator: (workFriendlyId, analysisType) =>
      `work-analysis-by-type:${workFriendlyId}:${analysisType}`,
  })
  public static async getWorkAnalysisByAnalysisType(
    workFriendlyId: string,
    analysisType: string
  ): Promise<WorkAnalysis | null> {
    // First get the work to ensure we have the correct internal ID
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (work && !work.tags?.toLowerCase().includes("demo")) {
          return null;
        }
      } else {
        throw err;
      }
    }
    if (!work) {
      throw new Error("Work not found");
    }

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", work.id)
      .eq("analysis_type", analysisType)
      .maybeSingle();

    if (error) {
      throw new Error(`Error fetching work analysis: ${error.message}`);
    }

    if (!data) {
      return null;
    }

    return WorkFactory.analysisToJSON(data);
  }

  /**
   * Get all analyses for a specific work
   * @param workFriendlyId - The ID or friendly ID of the work
   * @param state - Optional state to filter analyses
   * @returns Promise<WorkAnalysis[]> - Array of work analyses
   */
  // @Cacheable({
  //   ttl: 3600, // 1 hour cache
  //   keyGenerator: (workFriendlyId, state) =>
  //     `work-analyses:${workFriendlyId}:${state}`,
  // })
  public static async getWorkAnalyses(
    workFriendlyId: string,
    state?: string
  ): Promise<WorkAnalysis[]> {
    // First get the work to ensure we have the correct internal ID
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (work && !work.tags?.toLowerCase().includes("demo")) {
          return [];
        }
      } else {
        throw err;
      }
    }
    if (!work) {
      throw new Error("Work not found");
    }



    // get all active products
    const products = await WorkDb.getActiveProducts();

    if (!products) {
      throw new Error("Products not found");
    }

    const productIds = products.map((product) => product.code_name);

    let query = WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", work.id)
      .in("analysis_type", productIds);

    // Add optional filters
    if (state !== undefined) {
      query = query.eq("state", state);
    }

    const { data, error } = await query
      .order("analysis_category")
      .order("analysis_type");

    if (error) {
      throw error;
    }

    return (data || []).map((row) => WorkFactory.analysisToJSON(row));
  }

  /**
   * Get manuscript content for a work
   * @param workId - The ID of the work
   * @returns Promise<Section[]> - Array of sections containing manuscript content
   */
  @Cacheable({
    ttl: 3600, // 1 hour cache
    keyGenerator: (workId) => `manuscript-content:${workId}`,
  })
  public static async getManuscriptContent(
    workId: string
  ): Promise<Section | null> {
    const { data, error } = await WorkDb.getClient()
      .from("sections")
      .select("*")
      .eq("work_id", workId)
      .eq("friendly_id", "manuscript")
      .maybeSingle();

    if (error) {
      throw new Error(`Error fetching manuscript content: ${error.message}`);
    }

    // get the first item
    if (!data) {
      return null;
    }

    // Transform the data to match our expected format
    return WorkFactory.sectionToJSON(data);
  }

  /**
   * Update analysis for a work
   * @param id - The ID of the work
   * @param analysis - The analysis to update
   * @param markdown - The markdown to update
   * @param remote_file_metadata - The remote file metadata to update
   * @returns Promise<any> - The updated analysis
   */
  public static async updateAnalysis(
    id: string,
    analysis?: string,
    markdown?: string,
    remote_file_metadata?: Record<string, any>,
    errorMessage?: string,
    state?: WorkAnalysisState,
    metaData?: LLMMetadata,
    score?: number
  ): Promise<any> {
    try {
      const updateData: any = {};

      updateData["updated_at"] = new Date();

      // Add analysis to update data if provided
      if (analysis) {
        updateData["analysis_json"] = analysis;
      }

      if (markdown) {
        updateData["analysis_md"] = markdown;
      }

      if (remote_file_metadata) {
        updateData["remote_file_metadata"] = remote_file_metadata;
      }

      if (errorMessage) {
        updateData["error"] = errorMessage;
      }

      if (state) {
        updateData["state"] = state;
      }

      if (metaData) {
        updateData["metadata"] = metaData;
      }
      if (score !== undefined && score !== null && !isNaN(score)) {
        updateData["score"] = score;
      }

      // Perform the update in Supabase
      const { data, error } = await WorkDb.getClient()
        .from("work_analyses")
        .update(updateData)
        .eq("id", id)
        .select();

      if (error) {
        throw new Error(`Error updating analysis: ${error.message}`);
      }

      return data?.[0]; // Return the updated analysis data
    } catch (error) {
      console.error("Error updating analysis:", error);
      throw error;
    }
  }

  /** Get all analyses for a specific work */
  public static async getAnlaysesForWork(workId: string, state?: string) {
    if (!workId) {
      throw new Error("Missing workId");
    }

    if (state) {
      const { data, error } = await WorkDb.getClient()
        .from("work_analyses")
        .select("*")
        .eq("work_id", workId)
        .eq("state", state); // Filter by state

      if (error) {
        throw new Error(`Error fetching work analyses: ${error.message}`);
      }

      // Transform the data to match our expected format
      return (data || []).map((row) => WorkFactory.analysisToJSON(row));
    } else {
      const { data, error } = await WorkDb.getClient()
        .from("work_analyses")
        .select("*")
        .eq("work_id", workId);

      if (error) {
        throw new Error(`Error fetching work analyses: ${error.message}`);
      }
      // Transform the data to match our expected format
      return (data || []).map((row) => WorkFactory.analysisToJSON(row));
    }
  }

  /** Add analysis to the database */
  public static async addAnalysis(
    analysis: WorkAnalysis
  ): Promise<WorkAnalysis> {
    // Add a uuid if it doesn't exist
    if (!analysis.id) {
      analysis.id = v4();
    }

    const dataToInsert = {
      id: analysis.id,
      work_id: analysis.work_id,
      analysis_category: analysis.analysis_category,
      analysis_type: analysis.analysis_type,
      analysis_json: analysis.analysis_json,
      analysis_md: analysis.analysis_md,
      error: analysis.error,
      created_at: analysis.created_at,
      updated_at: analysis.updated_at,
      remote_file_metadata: analysis.remote_file_metadata,
      state: analysis.state,
      metadata: analysis.metadata,
      score: analysis.score,
    };

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .insert(dataToInsert)
      .select("*")
      .single();

    if (error) {
      throw new Error(`Error adding work analysis: ${error.message}`);
    }

    if (!data) {
      throw new Error(`Error adding work analysis: No data returned`);
    }

    return WorkFactory.analysisToJSON(data);
  }

  /**
   * Delete a specific analysis by ID
   * @param workId - The ID of the work
   * @param analysisId - The ID of the analysis to delete
   * @returns Promise<void>
   */
  public static async deleteAnalysis(
    workId: string,
    analysisId: string
  ): Promise<void> {
    const client = this.getClientInstance();
    if (!client) {
      throw new Error("Database client is not initialized");
    }

    const { error } = await client
      .from("work_analyses")
      .delete()
      .match({ work_id: workId, id: analysisId });

    if (error) {
      throw new Error(`Failed to delete analysis: ${error.message}`);
    }
  }

  /**
   * Subscribe to real-time updates for work analyses
   * @param workId - The ID of the work to subscribe to
   * @param callbacks - Callback functions for different events
   * @returns RealtimeChannel - The subscription channel
   */
  public static subscribeToWorkAnalyses(
    workId: string,
    callbacks: {
      onUpdate?: (payload: any) => void;
      onInsert?: (payload: any) => void;
      onDelete?: (payload: any) => void;
    }
  ) {
    const client = WorkDb.getClientInstance();
    if (!client) {
      throw new Error("Database client is not initialized");
    }

    return client
      .channel(`work_analyses_changes_${workId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "work_analyses",
          filter: `work_id=eq.${workId}`,
        },
        (payload) => {
          console.log("Received INSERT event:", payload);
          if (callbacks.onInsert) {
            callbacks.onInsert(payload);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "work_analyses",
          filter: `work_id=eq.${workId}`,
        },
        (payload) => {
          console.log("Received UPDATE event:", payload);
          if (callbacks.onUpdate) {
            callbacks.onUpdate(payload);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "work_analyses",
          filter: `work_id=eq.${workId}`,
        },
        (payload) => {
          console.log("Received DELETE event:", payload);
          if (callbacks.onDelete) {
            callbacks.onDelete(payload);
          }
        }
      )
      .subscribe((status) => {
        console.log("Subscription status:", status);
      });
  }
  // @Cacheable({
  //   ttl: 3600, // 1 hour cache
  //   keyGenerator: () => `static-content-keys`,
  // })
  public static async getStaticContentKeys(): Promise<string[]> {
    return repoGetStaticContentKeys(WorkDb.getClient());
  }

  public static async getStaticContentByKey(
    key: string
  ): Promise<{ id: string; value: string; version: number }> {
    return repoGetStaticContentByKey(WorkDb.getClient(), key);
  }

  public static async saveStaticContent(
    id: string,
    value: string
  ): Promise<{ id: string; value: string; version: number }> {
    return repoSaveStaticContent(WorkDb.getClient(), id, value);
  }

  /** Loads the character analysis from the database with the given ID */
  public static async getCharacterById(workId: string, characterId: string) {
    const analysisType = `character-analysis-${characterId}`;

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", workId)
      .eq("analysis_type", analysisType)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      throw new Error(
        `No character analysis found for character: ${characterId}`
      );
    }

    // the analysis will be in the analysis_json column
    if (!data.analysis_json) {
      throw new Error(
        `No character analysis found for character: ${characterId}`
      );
    }

    return data.analysis_json;
  }
  /** Loads the chapter summary from the database with the given ID */
  public static async getChapterByFriendlyId(
    workId: string,
    chapterFriendlyId: string
  ) {
    const analysisType = `chapter-summary-${chapterFriendlyId}`;

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", workId)
      .eq("analysis_type", analysisType)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      throw new Error(
        `No character analysis found for chapter: ${chapterFriendlyId}`
      );
    }

    // the analysis will be in the analysis_json column
    if (!data.analysis_json) {
      throw new Error(
        `No character analysis found for chapter: ${chapterFriendlyId}`
      );
    }

    // Sometimes the anlaysis property is an array with one item
    // If that's the case then return the first item
    if (Array.isArray(data.analysis_json.analysis)) {
      data.analysis_json.analysis = data.analysis_json.analysis[0];
    }

    return data.analysis_json;
  }
  /**
   * Get completed analyses for a work that are in the allowed analysis types.
   *
   * @param workId - The ID of the work
   * @param allowedAnalysisTypes - Array of analysis types to include
   * @returns Promise<WorkAnalysis[]> - Array of completed work analyses
   */
  public static async getCompletedShowAnalyses(
    workId: string,
    allowedAnalysisTypes: string[]
  ): Promise<WorkAnalysis[]> {
    if (!workId) {
      throw new Error("Missing workId");
    }

    const { data, error } = await WorkDb.getClient()
      .from("work_analyses")
      .select("*")
      .eq("work_id", workId)
      .eq("state", "completed")
      .not("analysis_md", "is", null)
      .in("analysis_type", allowedAnalysisTypes);

    if (error) {
      throw new Error(
        `Error fetching completed work analyses: ${error.message}`
      );
    }

    return (data || []).map((row) => WorkFactory.analysisToJSON(row));
  }

  /**
   * Parse tokens in a string and replace them with their corresponding values.
   * @param content - The content to parse
   * @returns Promise<string> - The parsed content with all tokens replaced
   */
  public static async parseTokens(content: string): Promise<string> {
    /*
     * Token Format: {{token_name}}
     * - Tokens must start with {{ and end with }}
     * - Can contain any characters except closing braces
     * - Whitespace around the token name is trimmed
     *
     * Regex Pattern Explanation: /\{\{([^}]+)\}\}/g
     * - \{\{ matches the literal characters {{
     * - [^}]+ matches one or more characters that are NOT a closing brace }
     *   - [^}] is a negated character set: match any char except }
     *   - + means match one or more of these characters
     * - \}\} matches the literal characters }}
     * - /g flag means match all occurrences globally
     *
     * Examples of valid tokens:
     * - {{simple}}
     * - {{prompt:fiction-disclaimer}}
     * - {{path/to/nested/token}}
     * - {{any.special@chars#here}}
     * - {{multiple words with spaces}}
     */
    const tokenRegex = /\{\{([^}]+)\}\}/g;
    let result = content;
    const matches = [...content.matchAll(tokenRegex)];
    for (const match of matches) {
      const token = match[1].trim(); // Trim whitespace from token
      try {
        const { value: tokenContent } = await WorkDb.getStaticContentByKey(
          token
        );
        const parsedContent = await this.parseTokens(tokenContent);
        result = result.replace(`{{${match[1]}}}`, parsedContent); // Use original match to preserve spacing
      } catch (error) {
        console.error(`Failed to parse token ${token}:`, error);
      }
    }

    return result;
  }

  // @Cacheable({
  //   ttl: 3600, // 1 hour cache
  //   keyGenerator: (userId) => `user-profile:${userId}`,
  // })
  public static async getUserProfile(userId: string) {
    const client = WorkDb.getClientInstance();
    if (!client) {
      throw new Error("Database client is not initialized");
    }

    const { data, error } = await client
      .from("user_profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }

    return data;
  }

  public static async createUserProfileIfNotExists(user: {
    id: string;
    email?: string;
  }) {
    const client = WorkDb.getClientInstance();
    if (!client) {
      throw new Error("Database client is not initialized");
    }
    return repoCreateUserProfileIfNotExists(client, user);
  }

  @Cacheable({
    ttl: 3600, // 1 hour cache
    keyGenerator: (userId) => `user-admin:${userId}`,
  })
  public static async isUserAdmin(userId: string): Promise<boolean> {
    const client = WorkDb.getClient();
    return repoIsUserAdmin(client, userId);
  }

  public static invalidateUserCache(userId: string) {
    repoInvalidateUserCache(userId);
  }

  public static clearUserCaches() {
    repoClearUserCaches();
  }

  /**
   * Approves a user by setting their is_approved field to true in the user_profiles table
   * @param userId The UUID of the user to approve
   * @returns The updated user profile or null if not found
   */
  public static async approveUser(userId: string): Promise<any> {
    const client = WorkDb.getClient();
    return repoApproveUser(client, userId);
  }


  /**
   * Handle a successful payment from Stripe
   * @param stripePaymentSession - The Stripe payment session object
   * @description
   * This function handles a successful payment from Stripe by creating a new payment record in the payments table.
   * It will also update the order and order_details table.
   * - The `orders` table will have a new record that indicates that payment has been made and the associated product id. This order number will be sent back out to the UI so that we can validate payment.
   * - The `order_details` table will have a new record for every product in the payment tier
   */
  public static async handleSuccessfulPayment(
    userId: string,
    paymentTier: PaymentTier,
    stripePaymentSession: any,
    workFriendlyId?: string,
    productCodeId?: string,
    specificProduct?: Product
  ): Promise<any> {
    const client = WorkDb.getClient();
    return repoHandleSuccessfulPayment(
      client,
      userId,
      paymentTier,
      stripePaymentSession,
      WorkDb.getProductTier.bind(WorkDb),
      WorkDb.getProductsByPaymentTier.bind(WorkDb),
      WorkDb.getWorkByFriendlyId.bind(WorkDb),
      workFriendlyId,
      productCodeId,
      specificProduct
    );
  }

  /**
   * Associates a work with an order ID
   * @param workId - The ID of the work to associate
   * @param orderId - The ID of the order to associate
   */
  public static async associatedWorkWithOrderId(workId: string, orderId: string): Promise<Order | null> {
    const client = WorkDb.getClient();
    return repoAssociatedWorkWithOrderId(client, workId, orderId);
  }

  /**
   * Handle a failed payment from Stripe
   * @param stripePaymentIntent - The Stripe payment intent object
   */
  public static async handleFailedPayment(
    userId: string,
    paymentTier: PaymentTier,
    paymentSession: any,
    workId?: string
  ): Promise<any> {
    const client = WorkDb.getClient();
    return repoHandleFailedPayment(client, userId, paymentTier, paymentSession, workId);
  }

  public static async handlePaymentEventReceived(
    type: string,
    createdAt: Date,
    object: string,
    apiVersion: string,
    livemode: boolean,
    pendingWebhooks: number,
    request: any,
    data: any
  ): Promise<any> {
    const client = WorkDb.getClient();
    return repoHandlePaymentEventReceived(
      client,
      type,
      createdAt,
      object,
      apiVersion,
      livemode,
      pendingWebhooks,
      request,
      data
    );
  }

  /**
   * Verifies a payment has been accepted by checking the 
   * orders table for a record that matches the user ID and payment tier
   * for a null work ID (which indicates payment has been accepted but not work uploaded)
   * @param userId The user ID
   * @param paymentTier The payment tier
   * @returns The payment record
   */
  public static async verifyPayment(
    userId: string,
    paymentTier: PaymentTier,
    workId?: string
  ): Promise<any> {
    const client = WorkDb.getClient();
    return repoVerifyPayment(
      client,
      userId,
      paymentTier,
      WorkDb.getProductTier.bind(WorkDb),
      WorkDb.getWorkByFriendlyId.bind(WorkDb),
      workId
    );
  }

  /**
   * Adds a record to the `background_tasks` table
   * and returns the record
   */
  public static async addBackgroundTask(
    type: TaskType,
    initialStatus: BackgroundTaskStatus = "notstarted",
    parameters: TaskParameters
  ): Promise<BackgroundTask | null> {
    const client = WorkDb.getClient();
    return repoAddBackgroundTask(client, type, initialStatus, parameters);
  }

  /**
   * Updates a background task record
   */
  public static async updateBackgroundTask(
    id: string,
    status: BackgroundTaskStatus,
    result: string = "",
    error: string = "",
    retry_count?: number,
    next_retry_at?: Date | null,
    metadata?: Record<string, any>
  ): Promise<BackgroundTask | null> {
    const client = WorkDb.getClient();
    return repoUpdateBackgroundTask(
      client,
      id,
      status,
      result,
      error,
      retry_count,
      next_retry_at,
      metadata
    );
  }

  /**
   * Gets the next pending task that is ready to be processed
   */
  public static async getNextPendingTask(): Promise<BackgroundTask | null> {
    const client = WorkDb.getClient();
    return repoGetNextPendingTask(client);
  }

  /**
   * Gets the task with the specified ID
   */
  public static async getBackgroundTask(
    id: string
  ): Promise<BackgroundTask | null> {
    const client = WorkDb.getClient();
    return repoGetBackgroundTask(client, id);
  }

  /**
   * Retrieve the latest workflow run and its child tasks for a work.
   */
  public static async getLatestWorkflowRun(
    workId: string
  ): Promise<{ parent: BackgroundTask; children: BackgroundTask[] } | null> {
    const client = WorkDb.getClient();
    return repoGetLatestWorkflowRun(client, workId);
  }

  /**
   * Get the roadmap summary for the specified work
   */
  @Cacheable({
    ttl: 14400, // cache for 4 hours for demo access
    keyGenerator: (friendlyId) => `roadmap-summary:${friendlyId}`,
  })
  public static async getRoadmapSummary(
    workFriendlyId: string
  ): Promise<any | null> {
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (!work || !work.tags?.toLowerCase().includes("demo")) {
          return null;
        }
      } else {
        throw err;
      }
    }

    if (!work) {
      throw new Error(`Work not found with id: ${workFriendlyId}`);
    }

    // get all analyses for a specific work
    const roadmapSummaryAnalysis = await WorkDb.getWorkAnalysisByAnalysisType(
      workFriendlyId,
      "roadmap-summary"
    );

    // validation
    if (!roadmapSummaryAnalysis) {
      console.warn(
        `[WorkDb.getRoadmapSummary] Could not find roadmap-summary analysis for work (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
      );
      return null;
    }

    if (
      !roadmapSummaryAnalysis.state ||
      roadmapSummaryAnalysis.state !== "completed"
    ) {
      console.warn(
        `[WorkDb.getRoadmapSummary] Roadmap summary is not in a completed state (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id}, State: ${roadmapSummaryAnalysis.state})`
      );
      return null;
    }

    // The primary storage for the analysis is in the analysis_json field
    // but we also store the analysis_md field for backwards compatibility
    // with older versions of the analysis workflow
    let reportContentToParse: string | null = null;

    if (!roadmapSummaryAnalysis.analysis_json || Object.keys(roadmapSummaryAnalysis.analysis_json).length === 0) {
      console.warn(
        `[WorkDb.getRoadmapSummary] Roadmap summary was completed but the analysis_json was not found or was empty (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id}, State: ${roadmapSummaryAnalysis.state})`
      );

      // the report is a string with two sections, <REASONING> and <REPORT>
      // We need to parse the <REPORT> section into a json object
      const reportContent = roadmapSummaryAnalysis.analysis_md.match(
        /<REPORT>([\s\S]*?)<\/REPORT>/
      );
      if (!reportContent || reportContent.length !== 2) {
        console.warn(
          `[WorkDb.getRoadmapSummary] Could not parse roadmap-summary analysis for work (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
        );
        return null;
      }

      reportContentToParse = reportContent[1];
    } else {
      reportContentToParse = roadmapSummaryAnalysis.analysis_json;
    }

    return reportContentToParse ? parseJSON(reportContentToParse) : null;
  }

  /**
   * Gets the roadmap summary for the specified work
   */
  @Cacheable({
    ttl: 14400,
    keyGenerator: (friendlyId) => `roadmap-summary-chapter:${friendlyId}`,
  })
  public static async getRoadmapSummaryByChapter(
    workFriendlyId: string
  ): Promise<any | null> {
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (!work || !work.tags?.toLowerCase().includes("demo")) {
          return null;
        }
      } else {
        throw err;
      }
    }

    if (!work) {
      throw new Error(`Work not found with id: ${workFriendlyId}`);
    }

    // get all analyses for a specific work
    const roadmapSummaryAnalysisByChapter =
      await WorkDb.getWorkAnalysisByAnalysisType(
        workFriendlyId,
        "roadmap-summary-by-chapter"
      );

    // validation
    if (!roadmapSummaryAnalysisByChapter) {
      console.warn(
        `[WorkDb.getRoadmapSummary] Could not find roadmap-summary-by-chapter analysis for work (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
      );
      return null;
    }

    if (
      !roadmapSummaryAnalysisByChapter.state ||
      roadmapSummaryAnalysisByChapter.state !== "completed"
    ) {
      console.warn(
        `[WorkDb.getRoadmapSummary] Roadmap summary by chapter is not in a completed state (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id}, State: ${roadmapSummaryAnalysisByChapter.state})`
      );
      return null;
    }

    // the chapter summaries are in a property called analysis_json
    const chapterSummaries = roadmapSummaryAnalysisByChapter.analysis_json;
    const chapterSummariesOutput: Record<string, any> = {};

    for (const chapterSummaryKey of Object.keys(chapterSummaries)) {

      // The chapter summary needs to be parsed from the <REPORT> section of the analysis_md
      const chapterAnalysisMarkdown =
        chapterSummaries[chapterSummaryKey]?.analysis_md;

      if (!chapterAnalysisMarkdown) {
        console.warn(
          `[WorkDb.getRoadmapSummary] Could not find analysis_md for chapter summary (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id}, ChapterSummaryKey: ${chapterSummaryKey})`
        );
        continue;
      }

      const reportContent = chapterAnalysisMarkdown.match(
        /<REPORT>([\s\S]*?)<\/REPORT>/
      );
      if (!reportContent || reportContent.length !== 2) {
        console.warn(
          `[WorkDb.getRoadmapSummary] Could not parse the REPORT section roadmap-summary-by-chapter analysis for work (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
        );
        continue;
      }

      let reportJson: Record<string, any> = {};
      try {
        // parse the report content into a json object
        reportJson = parseJSON(reportContent[1], true);

        if (!reportJson || Object.keys(reportJson).length === 0) {
          console.warn(
            `[WorkDb.getRoadmapSummary] Could not parse the REPORT section into roadmap-summary-by-chapter analysis for work (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
          );
          continue;
        }
      } catch (error) {
        console.warn(
          `[WorkDb.getRoadmapSummary] Could not parse REPORT roadmap-summary-by-chapter, gracefully continuing (Chapter: ${chapterSummaryKey}, WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
        );
        continue;
      }

      // Metrics
      // const metricsContent = chapterAnalysisMarkdown.match(
      //   /<METRICS>([\s\S]*?)<\/METRICS>/
      // );
      // if (!metricsContent || metricsContent.length !== 2) {
      //   console.warn(
      //     `[WorkDb.getRoadmapSummary] Could not parse METRICS due to metricsContent being empty. (Chapter: ${chapterSummaryKey}, WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
      //   );
      //   // Capture a Sentry breadcrumb when METRICS are missing so that we can diagnose
      //   // backend processing failures that lead to missing chapter metrics.
      //   Sentry.captureMessage("Missing METRICS in chapter analysis markdown", {
      //     level: "warning",
      //     extra: {
      //       workId: work.id,
      //       workFriendlyId,
      //       chapterSummaryKey,
      //     },
      //   });
      // } else {
      //   let metricsJson = {};
      //   try {
      //     // parse the metrics content into a json object
      //     metricsJson = parseJSON(metricsContent[1]);
      //     if (!metricsJson) {
      //       console.warn(
      //         `[WorkDb.getRoadmapSummary] Could not parse METRICS due to metricsJSON being empty. (WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
      //       );
      //       Sentry.captureMessage("Parsed METRICS is empty", {
      //         level: "warning",
      //         extra: {
      //           workId: work.id,
      //           workFriendlyId,
      //           chapterSummaryKey,
      //         },
      //       });
      //     } else {
      //       // we have valid metrics - attach them to the report
      //       reportJson.metrics = metricsJson;
      //     }
      //   } catch (error) {
      //     console.warn(
      //       `[WorkDb.getRoadmapSummary] Could not parse METRICS due to error ${error}. (Chapter: ${chapterSummaryKey}, WorkFriendlyId: ${workFriendlyId}, WorkId: ${work.id})`
      //     );
      //     Sentry.captureException(error as Error, {
      //       extra: {
      //         workId: work.id,
      //         workFriendlyId,
      //         chapterSummaryKey,
      //       },
      //     });
      //   }
      // }

      chapterSummariesOutput[chapterSummaryKey] = reportJson;
    }

    const { data, error } = await WorkDb.getClient()
      .from("works")
      .select("metadata")
      .eq("friendly_id", workFriendlyId)
      .single();

    if (error) {
      throw new Error(`Error getting work metadata: ${error.message}`);
    }

    const chapters = data.metadata?.chapters || [];

    // now add the chapter title to the existing chapters in roadmapSummaryAnalysisByChapter.analysis_json
    const chapter_keys = Object.keys(chapterSummariesOutput);

    // iterate through the chapter_keys and pull out the correct chapter name
    // The key will be "roadmap-summary-by-chapter:chapter-21---cassie" - we need to pull out the chapter name
    // which is the part after the comma
    // then we need to match it to the chapter_friendly_id in the chapters array
    // to be able to finally add the chapter title to the analysis_json
    chapter_keys.forEach((key: string) => {
      const chapter_friendly_id = key.split(":")[1];
      const chapter = chapters.find(
        (c: any) => c.friendly_id === chapter_friendly_id
      );

      if (!chapter) {
        console.warn(
          `[WorkDb.getRoadmapSummaryByChapter] Could not find chapter with friendly_id: ${chapter_friendly_id}`
        );
        return;
      }
      // first determine if the chapter title starts with "Chapter <number> -"
      // If so, remove that
      chapter.chapter_title = chapter.chapter_title.replace(
        /^Chapter \d+\s*[-–]\s*/,
        ""
      );

      if (chapter) {
        chapterSummariesOutput[key].chapter_title = toTitleCase(
          chapter.chapter_title
        );
      }
    });

    return chapterSummariesOutput;
  }

  // Products & Feedback wrappers
  @Cacheable({
    ttl: 3600,
    keyGenerator: () => `products-active`,
  })
  public static async getActiveProducts(): Promise<Product[] | null> {
    return getActiveProducts(WorkDb.getClient());
  }

  /**
   * Get the current demo book with caching (4 hour TTL)
   * @returns The current demo book or null if none available
   */
  @Cacheable({
    ttl: 14400, // 4 hours in seconds
    keyGenerator: () => `current-demo-book`,
  })
  public static async getCurrentDemoBook(): Promise<Work | null> {
    return getCurrentDemoBook(WorkDb.getClient());
  }

  /**
   * Returns the list of products the user has purchased for this work
   * @param workFriendlyId - Friendly ID of the work
   * @returns Array of products with their task status
   */
  public static async getUserProducts(
    workFriendlyId: string
  ): Promise<ProductWithTaskStatus[]> {
    let work: Work | null;
    try {
      work = await WorkDb.getWorkByFriendlyId(workFriendlyId);
    } catch (err: any) {
      if (err.message && err.message.includes("No authenticated user")) {
        work = await WorkDb.getWorkByFriendlyId(
          workFriendlyId,
          WorkDb.backgroundTaskClientId,
        );
        if (!work || !work.tags?.toLowerCase().includes("demo")) {
          return [];
        }
      } else {
        throw err;
      }
    }
    if (!work) {
      throw new Error(`Work not found for friendly_id: ${workFriendlyId}`);
    }

    const isDemo = work.tags?.toLowerCase().includes("demo") ?? false;

    if (isDemo) {
      const activeProducts = await WorkDb.getActiveProducts();
      if (!activeProducts || activeProducts.length === 0) {
        return [];
      }

      const { data: backgroundTasks, error: backgroundTasksError } =
        await WorkDb.getClient().rpc("get_tasks_for_work_id", { p_work_id: work.id });

      if (backgroundTasksError) {
        console.error(
          `Error getting background tasks: ${backgroundTasksError.message}`
        );
        throw new Error(
          `Error getting background tasks: ${backgroundTasksError.message}`
        );
      }

      let products = await repoGetUserProducts(
        WorkDb.getClient(),
        work.id,
        activeProducts.map((p) => p.id),
        backgroundTasks as any
      );

      if (products && products.length > 0) {
        const { filterSupersededProducts } = await import("../productUtils");
        products = filterSupersededProducts(products);
      }

      return products;
    }

    return getUserProductsForWork(WorkDb.getClient(), workFriendlyId);
  }

  public static async getProductById(productId: string): Promise<Product | null> {
    return getProductById(WorkDb.getClient(), productId);
  }

  public static async getProductByCodeName(codeName: string): Promise<Product | null> {
    return getProductByCodeName(WorkDb.getClient(), codeName);
  }

  public static async purchaseProduct(
    workFriendlyId: string,
    productId: string
  ): Promise<any> {
    const userId = await WorkDb.getAuthenticatedUserId();
    const product = await WorkDb.getProductById(productId);
    if (!product) {
      throw new Error(`Product not found for id: ${productId}`);
    }
    const { data: existingOrders } = await WorkDb.getClient()
      .from('orders')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    const orderId = existingOrders && existingOrders.length > 0 ? existingOrders[0].id : undefined;
    return purchaseProduct(WorkDb.getClient(), userId, productId, product.price, orderId);
  }

  public static async getTierProducts(): Promise<Product[] | null> {
    return getTierProducts(WorkDb.getClient());
  }

  public static async getProductTier(paymentTier: PaymentTier): Promise<Product | null> {
    const activeProducts = await WorkDb.getActiveProducts();
    return getProductTier(activeProducts, paymentTier);
  }

  public static async getProductsByPaymentTier(paymentTier: PaymentTier): Promise<Product[] | null> {
    const activeProducts = await WorkDb.getActiveProducts();
    return getProductsByPaymentTier(activeProducts, paymentTier);
  }

  public static async getOrderById(orderId: string): Promise<Order | null> {
    return getOrderById(WorkDb.getClient(), orderId);
  }

  public static async getOrdersForWork(workId: string): Promise<Order[] | null> {
    return getOrdersForWork(WorkDb.getClient(), workId);
  }

  public static async getUnassociatedOrderForUser(): Promise<Order | null> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return getUnassociatedOrderForUser(WorkDb.getClient(), userId);
  }

  public static async getUnassociatedOrderWithProductForUser(): Promise<{
    order: Order;
    product: Product;
  } | null> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return getUnassociatedOrderWithProductForUser(WorkDb.getClient(), userId);
  }

  public static async createFeedback(
    feedback: Omit<Feedback, 'id' | 'user_id' | 'created_at' | 'updated_at'>
  ): Promise<Feedback> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return createFeedback(WorkDb.getClient(), userId, feedback, WorkDb.getWorkByFriendlyId);
  }

  public static async getFeedbackForUser(): Promise<Feedback[]> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return getFeedbackForUser(WorkDb.getClient(), userId);
  }

  public static async getFeedbackById(feedbackId: string): Promise<Feedback | null> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return getFeedbackById(WorkDb.getClient(), userId, feedbackId);
  }

  public static async updateFeedback(
    feedbackId: string,
    updates: Partial<Omit<Feedback, 'id' | 'user_id' | 'created_at'>>
  ): Promise<Feedback> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return updateFeedback(WorkDb.getClient(), userId, feedbackId, updates);
  }

  public static async deleteFeedback(feedbackId: string): Promise<void> {
    const userId = await WorkDb.getAuthenticatedUserId();
    return deleteFeedback(WorkDb.getClient(), userId, feedbackId);
  }

  public static async getFeedbackByLocation(
    locationPage: string,
    locationElement?: string
  ): Promise<Feedback[]> {
    return getFeedbackByLocation(WorkDb.getClient(), locationPage, locationElement);
  }

  public static async getAnalysisFeedbackStats(
    analysisId: string
  ): Promise<{ averageRating: number; totalFeedback: number }> {
    return getAnalysisFeedbackStats(WorkDb.getClient(), analysisId);
  }

  public static async getProductCode(
    code: string,
  ): Promise<ProductCode | null> {
    return repoGetProductCode(WorkDb.getClient(), code);
  }

  public static async redeemProductCode(
    code: string,
    userId: string,
  ): Promise<ProductCode | null> {
    return repoRedeemProductCode(WorkDb.getClient(), code, userId);
  }

  public static async createEmailSignup(
    email: string,
    firstName?: string,
  ): Promise<CreateEmailSignupResult> {
    return repoCreateEmailSignup(WorkDb.getClient(), email, firstName);
  }

  @Cacheable({
    ttl: 300,
    keyGenerator: () => `site-notifications-active`,
  })
  public static async getActiveSiteNotifications(): Promise<import("@/app/types/siteNotification").SiteNotification[]> {
    return repoGetActiveSiteNotifications(WorkDb.getClient());
  }
}

